import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { t } from "i18next";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import myDashboardApi from "../../../../api/dashboard/myDashboardApi";
import taskRestrictionHelper from "../../../../helpers/taskRestrictionHelper";
import { advancedTaskHelper } from "../../../../helpers";
import taskTypeIconHelper from "../../../../helpers/taskTypeIconHelper";
import { JourneyFormGroupDto } from "../../../../types/dtos/journeys";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { BaseUserDetailsDto } from "../../../../types/dtos/generic";
import AdvancedToDoListItemDto from "../../../../types/dtos/dashboards/right-bar/to-do-list/AdvancedToDoListItemDto";
import ToDoListTaskTypeDto from "../../../../types/dtos/dashboards/right-bar/to-do-list/ToDoListTaskTypeDto";
import { UserContextInterface } from "../../../../state/UserContext";
import TaskRequirementBadge from "../../../task-management/TaskRequirementBadge";
import AddAdvancedTaskPopup from "../../../tasks/advanced-tasks/AddAdvancedTaskPopup";
import ExistingAdvancedTaskPopup from "../../../tasks/advanced-tasks/ExistingAdvancedTaskPopup";
import ToDoListEmptyState from "../shared/ToDoListEmptyState";
import ToDoListDisplay from "../shared/ToDoListDisplay";
import { LoadingPlaceholderItem } from "../shared/LoadingPlaceholderItem";
import ToDoListVerticalCarousel from "../shared/ToDoListVerticalCarousel";
import ToDoListLockedState from "./AdvancedToDoListLockedState";
import ToDoListRestrictionBanner from "./AdvancedToDoListRestrictionBanner";

interface AdvancedToDoListProps {
  userContext: UserContextInterface;
  initialToDoItems: AdvancedToDoListItemDto[];
  taskTypes: ClientTaskType[];
  toDoListTaskTypes: ToDoListTaskTypeDto[];
  selectedTaskTypeId: string;
  formDetails: JourneyFormGroupDto | undefined | null;
  hasJourneyBeenSubmitted: boolean;
  updateTaskTypeCount(taskTypeId: string | number, count: number): void;
  onTasksLoadingChange?(loading: boolean): void;
}

function AdvancedToDoList({
  userContext,
  initialToDoItems,
  taskTypes,
  toDoListTaskTypes,
  selectedTaskTypeId,
  formDetails,
  hasJourneyBeenSubmitted,
  updateTaskTypeCount,
  onTasksLoadingChange,
}: AdvancedToDoListProps) {
  const auth = useAuth();
  const dashboardApi = new myDashboardApi(auth.user?.access_token);

  const tasksCacheRef = useRef<Map<string, AdvancedToDoListItemDto[]>>(
    new Map(),
  );
  // Flag used to avoid hitting the API on the first selectedTaskTypeId change to load data as
  // the initial data comes from the dashboard load API
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [tasks, setTasks] = useState<AdvancedToDoListItemDto[]>([]);
  const [isLockedDueToFeaturedJourney, setIsLockedDueToFeaturedJourney] =
    useState<boolean>(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState<boolean>(false);
  const [isExistingTaskModalOpen, setIsExistingTaskModalOpen] =
    useState<boolean>(false);
  const [selectedUserTaskId, setSelectedUserTaskId] = useState<string | null>(
    null,
  );

  const selectedTaskType = taskTypes.find((x) => x.id === selectedTaskTypeId);
  const selectedToDoTaskType = toDoListTaskTypes.find(
    (x) => x.advancedTaskTypeId === selectedTaskTypeId,
  );
  const isActivelyInReview = selectedToDoTaskType?.isActivelyInReview ?? false;
  const reachedMaximumTaskLimit =
    selectedTaskType?.maxTaskCount != null &&
    tasks.length >= selectedTaskType?.maxTaskCount;
  const showRestrictionBanner =
    selectedTaskType != undefined &&
    selectedTaskType.suppressDashboardCreateAndValidate
      ? false
      : selectedTaskType != null &&
        !taskRestrictionHelper.doesUserHaveTasksThatSatisfyRestrictions(
          tasks,
          selectedTaskType.minTaskCount,
          selectedTaskType.maxTaskCount,
          selectedTaskType.countRestriction,
          selectedTaskType.categories,
        ).satisfied;

  const hideAddNewButton =
    selectedTaskType != undefined &&
    selectedTaskType.suppressDashboardCreateAndValidate;

  const taskSubjectOwner: BaseUserDetailsDto = {
    firstName: userContext.user.firstName,
    fullName: userContext.user.fullName,
    userId: userContext.user.id,
  };

  useEffect(() => {
    if (selectedTaskType == undefined) {
      return;
    }

    if (isInitialLoad) {
      setIsInitialLoad(false);
      setTasks(initialToDoItems);
      tasksCacheRef.current.set(selectedTaskTypeId, initialToDoItems);
      return;
    }

    const cached = tasksCacheRef.current.get(selectedTaskTypeId);
    if (cached !== undefined) {
      setTasks(cached);
      setIsRefreshing(true);
    } else {
      setTasks([]);
      setIsLoading(true);
    }
    onTasksLoadingChange?.(true);

    dashboardApi.getAdvancedToDoListItems(
      selectedTaskTypeId,
      (data: AdvancedToDoListItemDto[]) => {
        tasksCacheRef.current.set(selectedTaskTypeId, data);
        setIsLoading(false);
        setIsRefreshing(false);
        setTasks(data);
        onTasksLoadingChange?.(false);
      },
      (error: any) => {
        console.error(error.message);
        setIsLoading(false);
        setIsRefreshing(false);
        onTasksLoadingChange?.(false);
      },
    );
  }, [selectedTaskTypeId]);

  useEffect(() => {
    if (formDetails && selectedTaskType) {
      let currentlyFeaturedInJourney = formDetails.forms.some((form) =>
        form.questions.some(
          (question) =>
            question.questionType === "ADVANCED-TASK-MANAGEMENT" &&
            question.taskManagementConfig?.advancedTaskTypeId ===
              selectedTaskType?.id,
        ),
      );

      // If currently in journey, but the journey has been submitted... unlock it and reload items
      if (currentlyFeaturedInJourney == true && hasJourneyBeenSubmitted) {
        currentlyFeaturedInJourney = false;
        dashboardApi.getAdvancedToDoListItems(
          selectedTaskTypeId,
          (data: AdvancedToDoListItemDto[]) => {
            tasksCacheRef.current.set(selectedTaskTypeId, data);
            setIsLoading(false);
            setIsRefreshing(false);
            setTasks(data);
            setIsLockedDueToFeaturedJourney(currentlyFeaturedInJourney);
            onTasksLoadingChange?.(false);
          },
          (error: any) => {
            console.error(error.message);
            setIsLoading(false);
            setIsRefreshing(false);
            onTasksLoadingChange?.(false);
          },
        );
      } else {
        setIsLockedDueToFeaturedJourney(currentlyFeaturedInJourney);
      }
    }
  }, [formDetails, selectedTaskType, hasJourneyBeenSubmitted]);

  const onSuccessfulTaskCreation = (userTaskId: string | null) => {
    refreshTaskData(userTaskId, (newItemCount: number) => {
      updateTaskTypeCount(selectedTaskTypeId, newItemCount);
    });
    setIsAddTaskModalOpen(false);
  };

  const handleItemClick = (id: string) => {
    setSelectedUserTaskId(id);
    setIsExistingTaskModalOpen(true);
  };

  const handleExistingModalOpenChange = (isOpen: boolean) => {
    setIsExistingTaskModalOpen(isOpen);
    if (!isOpen) {
      refreshTaskData(null, (newItemCount: number) => {
        setSelectedUserTaskId(null);
        updateTaskTypeCount(selectedTaskTypeId, newItemCount);
      });
    }
  };

  const refreshTaskData = (
    userTaskId: string | null,
    onSuccess: (newItemCount: number) => void,
  ) => {
    dashboardApi.getAdvancedToDoListItems(
      selectedTaskTypeId,
      (data: AdvancedToDoListItemDto[]) => {
        data = updateDataWithRelevantFlags(userTaskId, data);
        tasksCacheRef.current.set(selectedTaskTypeId, data);
        setIsLoading(false);
        setIsRefreshing(false);
        setTasks(data);
        onSuccess(data.length);
      },
      (error: any) => {
        console.error(error.message);
        setIsLoading(false);
      },
    );
  };

  const updateDataWithRelevantFlags = (
    userTaskId: string | null,
    data: AdvancedToDoListItemDto[],
  ) => {
    let hasBeenAddedFlagSet = false;
    let hasUpdatedFlagBeenSet = false;

    if (userTaskId != null) {
      hasBeenAddedFlagSet = true;
      data = data.map((x) => {
        if (x.id === userTaskId) {
          x.wasAdded = true;
        }
        return x;
      });
    }

    if (selectedUserTaskId != null) {
      hasUpdatedFlagBeenSet = true;
      data = data.map((x) => {
        if (x.id === selectedUserTaskId) {
          x.wasUpdated = true;
        }
        return x;
      });
    }

    if (hasBeenAddedFlagSet || hasUpdatedFlagBeenSet) {
      setTimeout(() => {
        setTasks((prev) =>
          prev.map((x) => ({
            ...x,
            wasAdded: x.id === userTaskId ? false : x.wasAdded,
            wasUpdated: x.id === selectedUserTaskId ? false : x.wasUpdated,
          })),
        );
      }, 2000);
    }
    return data;
  };

  const showSkeleton = isLoading && tasks.length === 0;

  return (
    <>
      <div className="flex flex-col h-full">
        {showSkeleton && (
          <>
            <LoadingPlaceholderItem />
            <LoadingPlaceholderItem />
            <LoadingPlaceholderItem />
          </>
        )}

        {!showSkeleton &&
          !isLockedDueToFeaturedJourney &&
          showRestrictionBanner &&
          selectedTaskType &&
          (selectedTaskType.minTaskCount != null ||
            selectedTaskType.maxTaskCount != null) && (
            <ToDoListRestrictionBanner
              selectedTaskType={selectedTaskType}
              hasActiveTasks={tasks.length > 0}
            />
          )}

        {!showSkeleton &&
          selectedTaskType != undefined &&
          !isLockedDueToFeaturedJourney && (
            <>
              {(selectedTaskType.minTaskCount != null ||
                selectedTaskType.maxTaskCount != null ||
                selectedTaskType.categories?.find(
                  (x) => x.minTaskCount || x.maxTaskCount,
                ) != null) && (
                <TaskRequirementBadge
                  containerClassNames="mb-2"
                  tasks={tasks}
                  minTasks={selectedTaskType.minTaskCount}
                  maxTasks={selectedTaskType.maxTaskCount}
                  countRestriction={selectedTaskType.countRestriction}
                  activeCategories={selectedTaskType.categories}
                  taskType={selectedTaskType}
                  isNonEmployeeViewer={false}
                  origin="OUTSIDE-JOURNEY"
                  fullLengthMode={true}
                  subtleMode
                  hideWhenValid
                />
              )}
            </>
          )}

        <div>
          {!showSkeleton && isLockedDueToFeaturedJourney && (
            <ToDoListLockedState
              selectedTaskType={selectedTaskType}
              journeyTitle={formDetails?.title}
            />
          )}
          {!showSkeleton &&
            !isLockedDueToFeaturedJourney &&
            selectedTaskType && (
              <div
                className={cx(
                  "transition-opacity duration-200 ease-out",
                  isRefreshing && "opacity-70 pointer-events-none",
                )}
              >
              {tasks.length === 0 && (
                <ToDoListEmptyState
                  iconType={taskTypeIconHelper.getTaskTypeIcon(
                    selectedTaskType.iconType,
                  )}
                  pluralNameTranslationKeyIdentifier={
                    selectedTaskType.pluralNameTranslationKeyIdentifier
                  }
                />
              )}
              {tasks.length > 0 && (
                <ToDoListVerticalCarousel
                  items={tasks}
                  resetKey={selectedTaskTypeId}
                  renderItems={(visibleItems) => (
                    <ToDoListDisplay
                      hideTaskTypeBadge
                      items={visibleItems}
                      iconType={taskTypeIconHelper.getTaskTypeIcon(
                        selectedTaskType.iconType,
                      )}
                      singularNameTranslationKeyIdentifier={
                        selectedTaskType.singularNameTranslationKeyIdentifier
                      }
                      onItemClick={handleItemClick}
                    />
                  )}
                />
              )}
              {selectedTaskType && !hideAddNewButton && (
                <>
                  <hr className="border-gray-200 my-3" />
                  <div className="flex justify-center">
                    <button
                      disabled={reachedMaximumTaskLimit}
                      className="inline-flex items-center primary-colour font-semibold text-sm bg-transparent border-0 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => setIsAddTaskModalOpen(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span className="ml-2">
                        {t("TaskType.Button.AddNewX").replace(
                          "#TASK_TYPE#",
                          advancedTaskHelper.ToLowerCase(
                            selectedTaskType.singularNameTranslationKeyIdentifier,
                          ),
                        )}
                      </span>
                    </button>
                  </div>
                </>
              )}
              </div>
            )}
        </div>
      </div>

      {selectedTaskType && (
        <>
          <AddAdvancedTaskPopup
            usageScenario="DASHBOARD-TASKS"
            taskType={selectedTaskType}
            activeTasks={tasks}
            maxOverallTaskRestriction={selectedTaskType.maxTaskCount ?? null}
            categories={selectedTaskType.categories ?? []}
            ownerEmployee={taskSubjectOwner}
            isOpen={isAddTaskModalOpen}
            onOpenChange={setIsAddTaskModalOpen}
            onSuccessfulCreation={onSuccessfulTaskCreation}
          />
          <ExistingAdvancedTaskPopup
            usageScenario="DASHBOARD-TASKS"
            taskType={selectedTaskType}
            userTaskId={selectedUserTaskId}
            categories={selectedTaskType.categories ?? []}
            isOpen={isExistingTaskModalOpen}
            onOpenChange={handleExistingModalOpenChange}
            onSuccessfulUpdate={() => handleExistingModalOpenChange(false)}
            onSuccessfulCancel={() => handleExistingModalOpenChange(false)}
            onSuccessfulClose={() => handleExistingModalOpenChange(false)}
            onSuccessfulReOpen={() => handleExistingModalOpenChange(false)}
            onSuccessfulSavedForLater={() =>
              handleExistingModalOpenChange(false)
            }
            onCommentCountChanged={() => handleExistingModalOpenChange(false)}
            isLocked={isActivelyInReview}
          />
        </>
      )}
    </>
  );
}

export default AdvancedToDoList;
